// The builder's colour per goal and its icon set, in one place. Pages never name a goal
// colour themselves: they put goalVars(category) on an element and the classes in
// src/prompt-builder/styles.css read --g-fill, --g-on, --g-tint, --g-edge and --g-glyph from it.
//
// Every value is a solid Sked Social palette colour or a tint of one, at 100% opacity.
// Text sits on `fill` only in `onFill` (each pair is AA 4.5:1 or better) and on `tint` only
// in ink (15:1 or better). Accent hues are never used as text on white.

import type { DuotoneName } from "./duotone-icons";
import type { Category } from "./templates";

export interface GoalTheme {
  /** The goal's brand colour: selected chips, solid icon tiles, hero blocks, accent edges. */
  fill: string;
  /** Text and glyphs on `fill`. */
  onFill: string;
  /** Soft ground: section bands, unselected chips, quiet icon tiles. Ink text on it. */
  tint: string;
  /** Hairline on the tint. Decorative only, never text. */
  edge: string;
  /** Duotone icons on `tint` or white: 3:1 or better on both. Never text. */
  glyph: string;
}

const INK = "#101828";
const WHITE = "#FFFFFF";

export const GOAL_THEME: Record<Category, GoalTheme> = {
  // Cosmo pink, darkened to #C61565 so white text passes (5.68:1). The human colour, for stories.
  // Glyph 4.85:1 on the tint.
  "brand-story": {
    fill: "#C61565",
    onFill: WHITE,
    tint: "#FEE7F2",
    edge: "#FAA3CA",
    glyph: "#C61565",
  },
  // Blurple, white text 6.53:1. Sked Social's calm-authority colour. Glyph 5.54:1 on the tint.
  authority: { fill: "#4F31FF", onFill: WHITE, tint: "#EDEAFF", edge: "#B9ADFF", glyph: "#4F31FF" },
  // Miami, ink text 6.91:1. The bright social accent, for conversation starters. The fill is
  // 2.30:1 on its tint, so glyphs take Miami 600 (3.95:1).
  engagement: { fill: "#EF72E2", onFill: INK, tint: "#FDEEFB", edge: "#F5B9EE", glyph: "#B84FAD" },
  // Signal lime, ink text 14.78:1. Lime is the "convert" signal in Sked Social. Glyphs take
  // Lime 700 (5.68:1 on the tint).
  conversion: { fill: "#D5F870", onFill: INK, tint: "#F7FFDF", edge: "#DDEF9F", glyph: "#586C1F" },
  // Spice, ink text 7.30:1. Warm energy, a record light for video. Glyphs take Spice 700
  // (4.62:1 on the tint).
  "short-video": {
    fill: "#FF852C",
    onFill: INK,
    tint: "#FFF3EA",
    edge: "#FFC9A0",
    glyph: "#AC581B",
  },
};

/** Inline custom properties for a goal, for server-rendered HTML. */
export function goalVars(category: Category): string {
  const theme = GOAL_THEME[category];
  return `--g-fill:${theme.fill};--g-on:${theme.onFill};--g-tint:${theme.tint};--g-edge:${theme.edge};--g-glyph:${theme.glyph}`;
}

/** The same properties as a React style object, so SSR and hydration render the same string. */
export function goalStyle(category: Category): Record<string, string> {
  const theme = GOAL_THEME[category];
  return {
    "--g-fill": theme.fill,
    "--g-on": theme.onFill,
    "--g-tint": theme.tint,
    "--g-edge": theme.edge,
    "--g-glyph": theme.glyph,
  };
}

/** Icons for the page sections around the prompts. */
export const SECTION_ICONS = {
  howGoal: "bullseye-pointer",
  howBrand: "pen-to-square",
  howPaste: "paste",
  faq: "circle-question",
  skedIdeas: "lightbulb-on",
  gate: "envelope-open-text",
  gatePack: "square-list",
  gateIdeas: "lightbulb-on",
  gatePdf: "file-pdf",
  packIdeas: "wand-magic-sparkles",
  pdf: "file-pdf",
  makeBoard: "thumbtack",
  yourBoards: "grid-2",
  expiry: "calendar-clock",
} as const satisfies Record<string, DuotoneName>;
